/**
 * Shared listing status helpers and components.
 * Used by both MarketplaceListingsSection (Epic 03) and ChannelListedProducts (Epic 04).
 */

import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

export function getStatusColor(status: string) {
  const s = status.toLowerCase();
  if (["activate", "active", "live"].includes(s))
    return { bg: "bg-green-100", text: "text-green-800" };
  if (["seller_deactivated", "deactivated", "inactive"].includes(s))
    return { bg: "bg-amber-100", text: "text-amber-800" };
  if (["platform_deactivated", "freeze", "frozen", "deleted", "rejected", "failed"].includes(s))
    return { bg: "bg-red-100", text: "text-red-800" };
  if (["draft", "pending"].includes(s))
    return { bg: "bg-blue-100", text: "text-blue-800" };
  return { bg: "bg-gray-100", text: "text-gray-600" };
}

const STATUS_LABELS: Record<string, string> = {
  activate: "Active",
  seller_deactivated: "Deactivated",
  platform_deactivated: "Suspended",
  freeze: "Frozen",
  draft: "Draft",
  pending: "Pending",
  failed: "Failed",
  deleted: "Deleted",
};

export function displayStatus(status: string): string {
  const s = status.toLowerCase();
  return STATUS_LABELS[s] || (s.charAt(0).toUpperCase() + s.slice(1));
}

export function ListingStatusPill({ status }: { status: string }) {
  const { bg, text } = getStatusColor(status);
  const label = displayStatus(status);
  return (
    <span
      className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", bg, text)}
      aria-label={`Status: ${label}`}
    >
      {label}
    </span>
  );
}

export function formatPrice(price: number, currency?: string): string {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: currency || "THB",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);
}

export function formatSyncTime(lastSynced: string): { relative: string; full: string } {
  try {
    const date = new Date(lastSynced);
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      full: date.toLocaleString(),
    };
  } catch {
    return { relative: "\u2014", full: "" };
  }
}
